import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {PageHeader} from '@common-web/components/page-header';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {AlertCircle, Settings, ToggleLeft} from 'lucide-react';
import {toast} from '@common-web/hooks/use-toast';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {
  INTERNAL_MENU_FLAGS,
  resolveInternalMenuItemEnabled,
  type InternalFeaturesShape,
  type InternalMenuKey,
} from '@common/menuFlags';
import type {AccountsViewConfig} from '@common/accountsView';
import {InternalFeatureFlagControls, type OverrideState} from './components/internalFeatureConfig';
import {AccountsViewConfigSection} from '../clients/components/accountsViewConfig';
import {OutboundEmailConfigSection} from './components/outboundEmailConfig';
import {FtpWritesConfigSection} from './components/ftpWritesConfig';
import type {ToggleableEmailEnvironment} from '@common/emailSending';
import type {ToggleableFtpWriteEnvironment} from '@common/ftpWrites';

export function InternalFeatures() {
  const canEdit = useHasPermission(CODE_PERMISSIONS.internalConfig.manage.key);

  const {data, isLoading, isFetching, error, refetch} = trpc.internal.config.get.useQuery();

  const updateFeatures = trpc.internal.config.updateFeatures.useMutation({
    onSuccess: () => {
      toast({
        title: 'Internal features updated',
        description: 'Navigation visibility refreshes for internal staff on their next page load.',
      });
      refetch();
    },
    onError: createMutationErrorHandler('Error updating internal features'),
  });

  const features = (data?.features ?? {}) as InternalFeaturesShape;
  const isPending = updateFeatures.isPending;

  const overrideCount = Object.values(features.menuOverrides ?? {}).filter(
    (v) => typeof v === 'boolean',
  ).length;
  const visibleCount = INTERNAL_MENU_FLAGS.filter((f) =>
    resolveInternalMenuItemEnabled(f.key, features),
  ).length;

  const handleOverrideChange = (key: InternalMenuKey, next: OverrideState) => {
    const merged: Partial<Record<InternalMenuKey, boolean>> = {
      ...(features.menuOverrides ?? {}),
    };

    if (next === 'default') {
      delete merged[key];
    } else {
      merged[key] = next === 'on';
    }

    updateFeatures.mutate({features: {menuOverrides: merged}});
  };

  const handleAccountsViewSave = (config: AccountsViewConfig) => {
    updateFeatures.mutate({features: {accountsView: config}});
  };

  const handleAccountsViewReset = () => {
    updateFeatures.mutate({features: {accountsView: null}});
  };

  const handleOutboundEmailChange = (name: ToggleableEmailEnvironment, enabled: boolean) => {
    updateFeatures.mutate({
      features: {outboundEmail: {...(features.outboundEmail ?? {}), [name]: {enabled}}},
    });
  };

  const handleFtpWritesChange = (name: ToggleableFtpWriteEnvironment, enabled: boolean) => {
    updateFeatures.mutate({
      features: {ftpWrites: {...(features.ftpWrites ?? {}), [name]: {enabled}}},
    });
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Internal Features'
          description='Org-wide feature flags for the internal portal. Changes apply to every internal CBS staff user.'
          icon={<ToggleLeft className='h-6 w-6' />}
          isFetching={isFetching || isPending}
          isLoading={isLoading}
        >
          <Badge
            className={
              overrideCount > 0
                ? 'bg-amber/10 text-amber border-amber/20'
                : 'bg-white/10 text-white border-white/20'
            }
          >
            {overrideCount} override{overrideCount === 1 ? '' : 's'}
          </Badge>
          <Badge className='bg-white/10 text-white border-white/20'>
            {visibleCount} / {INTERNAL_MENU_FLAGS.length} visible
          </Badge>
        </PageHeader>

        <div className='px-8 py-6 max-w-7xl mx-auto w-full space-y-6'>
          {!canEdit && (
            <Alert className='bg-amber/5 border-amber/30'>
              <Settings className='h-4 w-4 text-amber' />
              <AlertTitle className='text-brand-navy'>View-only</AlertTitle>
              <AlertDescription className='text-brand-gray'>
                You can review internal feature flags here, but only internal admins can change
                them.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle>Could not load internal features</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className='space-y-4'>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className='h-32 w-full' />
              ))}
            </div>
          ) : (
            <div className='space-y-6'>
              <Card className='border-gray-200 shadow-sm'>
                <CardContent className='p-6'>
                  <AccountsViewConfigSection
                    value={features.accountsView}
                    disabled={!canEdit}
                    isPending={isPending}
                    scope='internal'
                    scopeLabel='Customize the Accounts view for the internal portal.'
                    onSave={handleAccountsViewSave}
                    onReset={handleAccountsViewReset}
                  />
                </CardContent>
              </Card>
              {data?.canManageOutboundEmail && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-6'>
                    <OutboundEmailConfigSection
                      value={features.outboundEmail}
                      currentEnvironment={data.environment}
                      disabled={!canEdit || isPending}
                      onChange={handleOutboundEmailChange}
                    />
                  </CardContent>
                </Card>
              )}
              {data?.canManageOutboundEmail && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-6'>
                    <FtpWritesConfigSection
                      value={features.ftpWrites}
                      currentEnvironment={data.environment}
                      disabled={!canEdit || isPending}
                      onChange={handleFtpWritesChange}
                    />
                  </CardContent>
                </Card>
              )}
              <Card className='border-gray-200 shadow-sm'>
                <CardContent className='p-6'>
                  <InternalFeatureFlagControls
                    features={features}
                    disabled={!canEdit || isPending}
                    onOverrideChange={handleOverrideChange}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
